import { useState } from "react";
import { db, auth, storage, functions } from "@/lib/firebase";
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc, onSnapshot, orderBy, Timestamp, setDoc, limit } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { dualWriteJobDocument } from "@/lib/firestore-helpers";
import { COMPLIANCE_CATEGORIES } from "./JobDocuments";
import { FileSearch, Loader2, X, FileText, Image } from "lucide-react";
import type { DocumentType, DocumentExtractionResult, ExtractedField } from "@/hooks/useCompliance";
import { validateFile } from "@/lib/security";
import { sanitizeAndTrim, sanitizeFileName } from "@/lib/sanitize";

// Map compliance categories to extraction-supported document types
// Keys must match database attachment_categories codes
const EXTRACTABLE_DOCUMENT_TYPES: Record<string, DocumentType | null> = {
  "contractor_license": "contractor_license",
  "insurance_coi": "certificate_of_insurance",
  "load_calculation": "manual_j_calculation",
  "product_approval": "noa_product_approval",
  "application_form": "permit_application",
  // These don't have extraction schemas yet
  "equipment_specs": null,
  "site_plan": null,
  "riser_diagram": null,
  "single_line": null,
  "energy_calc": null,
  "sealed_drawings": null,
  "owner_auth": null,
  "building_plans": null,
  "survey": null,
};

// Map compliance categories to generic document types (for storage/display)
// Keys must match database attachment_categories codes
const COMPLIANCE_TO_DOC_TYPE: Record<string, string> = {
  "application_form": "permit_doc",
  "contractor_license": "certificate",
  "insurance_coi": "certificate",
  "load_calculation": "calculation",
  "equipment_specs": "specification",
  "site_plan": "floor_plan",
  "riser_diagram": "floor_plan",
  "single_line": "floor_plan",
  "energy_calc": "calculation",
  "product_approval": "certificate",
  "sealed_drawings": "floor_plan",
  "owner_auth": "contract",
  "building_plans": "floor_plan",
  "survey": "floor_plan",
};

type UploadDocumentProps = {
  jobId: string;
  contractorId: string;
  onClose: () => void;
  onSuccess: (extractionResult?: DocumentExtractionResult) => void;
  initialCategory?: string;
  requirementName?: string;
};

export const UploadDocument = ({
  jobId,
  contractorId,
  onClose,
  onSuccess,
  initialCategory,
  requirementName
}: UploadDocumentProps) => {
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState({ current: 0, total: 0 });
  const [files, setFiles] = useState<File[]>([]);
  const [formData, setFormData] = useState({
    document_type: "floor_plan",
    compliance_category: initialCategory || "",
    description: "",
  });
  const [autoExtract, setAutoExtract] = useState(true);
  const { toast } = useToast();

  // Max files allowed for multi-page documents
  const MAX_FILES = 10;

  // Check if extraction is available for selected category
  const canExtract = formData.compliance_category &&
    EXTRACTABLE_DOCUMENT_TYPES[formData.compliance_category] !== null &&
    EXTRACTABLE_DOCUMENT_TYPES[formData.compliance_category] !== undefined;

  // SECURITY: Allowed file types for document uploads
  const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/tiff',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx', '.webp', '.tiff', '.tif'];
  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB max per file

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const validatedFiles: File[] = [];
      const errors: string[] = [];

      // SECURITY: Validate each file before accepting
      for (const file of newFiles) {
        const validation = validateFile(file, {
          maxSizeBytes: MAX_FILE_SIZE,
          allowedTypes: ALLOWED_FILE_TYPES,
          allowedExtensions: ALLOWED_EXTENSIONS,
        });

        if (validation.valid) {
          validatedFiles.push(file);
        } else {
          errors.push(`${file.name}: ${validation.error}`);
        }
      }

      // Show validation errors
      if (errors.length > 0) {
        toast({
          title: "Some files rejected",
          description: errors.join("; "),
          variant: "destructive",
        });
      }

      // Limit total files to MAX_FILES
      const combinedFiles = [...files, ...validatedFiles].slice(0, MAX_FILES);
      setFiles(combinedFiles);

      if (validatedFiles.length + files.length > MAX_FILES) {
        toast({
          title: "File limit reached",
          description: `Maximum ${MAX_FILES} files allowed. Some files were not added.`,
          variant: "destructive",
        });
      }
    }
  };

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return <FileText className="h-4 w-4 text-red-500" />;
    if (['jpg', 'jpeg', 'png', 'webp', 'tiff', 'tif'].includes(ext || '')) {
      return <Image className="h-4 w-4 text-blue-500" />;
    }
    return <FileText className="h-4 w-4 text-gray-500" />;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (files.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one file to upload",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Not authenticated");

      // SECURITY: Sanitize description before storage
      const sanitizedDescription = sanitizeAndTrim(formData.description, 1000);

      // Upload all files to storage
      const uploadedFiles: { name: string; url: string }[] = [];
      for (const file of files) {
        // SECURITY: Sanitize file name to prevent path traversal
        const safeFileName = sanitizeFileName(file.name);
        const fileExt = safeFileName.split(".").pop();
        const storagePath = `job-documents/${contractorId}/${jobId}/${Date.now()}-${safeFileName}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        uploadedFiles.push({ name: safeFileName, url: downloadURL });
      }

      // Primary document URL (first file) for backward compatibility
      const primaryUrl = uploadedFiles[0].url;
      const primaryName = files.length === 1
        ? uploadedFiles[0].name
        : `${uploadedFiles[0].name} (+${files.length - 1} more)`;

      // Determine document type: use compliance-derived type if category is set, else use selected type
      const effectiveDocType = formData.compliance_category
        ? (COMPLIANCE_TO_DOC_TYPE[formData.compliance_category] || formData.document_type)
        : formData.document_type;

      // Save document metadata using dual-write helper
      const documentId = doc(collection(db, "job_documents")).id;
      await dualWriteJobDocument(contractorId, jobId, documentId, {
        uploaded_by: user.uid,
        document_name: primaryName,
        document_url: primaryUrl,
        // Store all file URLs for multi-page documents
        all_file_urls: uploadedFiles.map(f => f.url),
        all_file_names: uploadedFiles.map(f => f.name),
        page_count: files.length,
        document_type: effectiveDocType,
        description: sanitizedDescription,
        // Compliance category for matching with checklist requirements
        category: formData.compliance_category || null,
        complianceCategory: formData.compliance_category || null,
        // Track upload status for compliance checking
        status: formData.compliance_category ? 'uploaded' : null,
        createdAt: Timestamp.now()
      });

      // Auto-extract if enabled and document type supports it
      let extractionResult: DocumentExtractionResult | undefined;
      if (autoExtract && canExtract && formData.compliance_category) {
        const extractionDocType = EXTRACTABLE_DOCUMENT_TYPES[formData.compliance_category];
        if (extractionDocType) {
          setLoading(false);
          setExtracting(true);
          setExtractionProgress({ current: 0, total: files.length });

          const pageText = files.length > 1 ? `${files.length} pages/files` : "your document";
          toast({
            title: "Extracting document data...",
            description: `AI is reading and extracting information from ${pageText}`,
          });

          try {
            // Call extraction with array of file URLs for multi-page support
            const extractDocumentData = httpsCallable<{
              documentId: string;
              documentType: string;
              fileUrl?: string;
              fileUrls?: string[];
              contractorId: string;
              jobId: string;
              maxPages?: number;
            }, DocumentExtractionResult & { pageCount?: number }>(functions, 'extractDocumentData');

            const result = await extractDocumentData({
              documentId,
              documentType: extractionDocType,
              // Use fileUrls array for multiple files, fileUrl for single
              ...(files.length > 1
                ? { fileUrls: uploadedFiles.map(f => f.url) }
                : { fileUrl: primaryUrl }
              ),
              contractorId,
              jobId, // Required for correct Firestore path
              maxPages: MAX_FILES,
            });

            extractionResult = result.data;
            setExtractionProgress({ current: result.data.pageCount || files.length, total: files.length });

            // Save extracted fields to document
            // Cloud Function returns extractedFields as an object, but UI expects an array
            if (extractionResult?.success && extractionResult.extractedFields) {
              // Convert object to array format for UI display
              const fieldsAsArray: ExtractedField[] = Object.entries(extractionResult.extractedFields)
                .map(([name, value]) => ({
                  name,
                  value: value as string | null,
                  confidence: extractionResult.confidence || 0,
                }));

              await dualWriteJobDocument(contractorId, jobId, documentId, {
                extractedFields: fieldsAsArray,
                extractionConfidence: extractionResult.confidence,
                extractionMethod: extractionResult.extractionMethod,
                extractedAt: Timestamp.now(),
                pagesProcessed: result.data.pageCount || files.length,
              });
            }

            // Count extracted fields (extractedFields is an object, not an array)
            const extractedFieldCount = extractionResult.extractedFields
              ? Object.values(extractionResult.extractedFields).filter(v => v !== null && v !== undefined && v !== "").length
              : 0;

            toast({
              title: "Extraction complete",
              description: `Extracted ${extractedFieldCount} fields from ${result.data.pageCount || files.length} page(s) with ${Math.round((extractionResult.confidence || 0) * 100)}% confidence`,
            });
          } catch (extractError: any) {
            console.error("Extraction error:", extractError);
            toast({
              title: "Extraction failed",
              description: "Document uploaded, but automatic extraction failed. You can try again later.",
              variant: "destructive",
            });
          }
        }
      } else {
        toast({
          title: "Success",
          description: files.length > 1
            ? `${files.length} files uploaded successfully`
            : "Document uploaded successfully",
        });
      }

      onSuccess(extractionResult);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setExtracting(false);
    }
  };

  // Show extraction in progress state
  if (extracting) {
    const progressText = extractionProgress.total > 1
      ? `Processing ${extractionProgress.total} pages/files...`
      : "AI is reading your document and extracting information...";

    return (
      <Dialog open onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="relative">
              <FileSearch className="h-12 w-12 text-primary" />
              <Loader2 className="h-6 w-6 text-primary absolute -bottom-1 -right-1 animate-spin" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold">Extracting Document Data</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {progressText}
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              {extractionProgress.total > 1
                ? "This may take up to 60 seconds for multi-page documents"
                : "This may take up to 30 seconds"
              }
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          {requirementName && (
            <DialogDescription>
              Uploading for: <span className="font-medium">{requirementName}</span>
            </DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="file">
              {canExtract ? "Files *" : "File *"}
              {canExtract && (
                <span className="text-xs font-normal text-muted-foreground ml-2">
                  (up to {MAX_FILES} files for multi-page documents)
                </span>
              )}
            </Label>
            <Input
              id="file"
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.webp,.tiff,.tif"
              multiple={canExtract}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Supported formats: PDF, JPG, PNG, DOC, DOCX, WEBP, TIFF
              {canExtract && " — Select multiple files for multi-page documents"}
            </p>

            {/* File list preview */}
            {files.length > 0 && (
              <div className="mt-3 space-y-2">
                <div className="text-sm font-medium">
                  Selected files ({files.length}/{MAX_FILES}):
                </div>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {files.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between p-2 bg-muted/50 rounded-md text-sm"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {getFileIcon(file.name)}
                        <span className="truncate">{file.name}</span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          ({(file.size / 1024).toFixed(0)} KB)
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 hover:bg-destructive/10"
                        onClick={() => removeFile(index)}
                        disabled={loading}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Compliance Category Selector */}
          <div>
            <Label htmlFor="compliance_category">Compliance Category</Label>
            <Select
              value={formData.compliance_category}
              onValueChange={(value) => setFormData({ ...formData, compliance_category: value === "none" ? "" : value })}
              disabled={loading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select compliance category (optional)" />
              </SelectTrigger>
              <SelectContent>
                {COMPLIANCE_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value || "none"} value={cat.value || "none"}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Select a category to link this document to permit requirements
            </p>
          </div>

          {/* Auto-extract option */}
          {canExtract && (
            <div className="flex items-center space-x-2 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
              <Checkbox
                id="autoExtract"
                checked={autoExtract}
                onCheckedChange={(checked) => setAutoExtract(checked === true)}
                disabled={loading}
              />
              <div className="flex-1">
                <Label htmlFor="autoExtract" className="text-sm font-medium cursor-pointer">
                  Auto-extract document data
                </Label>
                <p className="text-xs text-muted-foreground">
                  AI will read and extract key information from this document type
                </p>
              </div>
              <FileSearch className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          )}

          {/* Document Type - only show when NO compliance category is selected */}
          {!formData.compliance_category && (
            <div>
              <Label htmlFor="type">Document Type *</Label>
              <Select
                value={formData.document_type}
                onValueChange={(value) => setFormData({ ...formData, document_type: value })}
                disabled={loading}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="floor_plan">Floor Plan</SelectItem>
                  <SelectItem value="layout">Layout</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="photo">Photo</SelectItem>
                  <SelectItem value="permit_doc">Permit Document</SelectItem>
                  <SelectItem value="specification">Specification</SelectItem>
                  <SelectItem value="calculation">Calculation</SelectItem>
                  <SelectItem value="certificate">Certificate</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional description..."
              disabled={loading}
            />
          </div>

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || files.length === 0}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading {files.length > 1 ? `${files.length} files...` : "..."}
                </>
              ) : (
                files.length > 1 ? `Upload ${files.length} files` : "Upload"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
